import { Injectable } from '@angular/core';
import {
  CheckboxQuestion,
  CheckboxQuestionOptions,
  DatePickerControlOptions,
  DatePickerQuestion,
  DropdownControlOptions,
  DropdownQuestion,
  max,
  min,
  MultiselectControlOptions,
  MultiselectQuestion
} from "@ot/shared/questions";
import { LocalDate } from 'js-joda';
import { Jurisdictions } from '../constants';
import { TaxAccount, Vehicle } from '../models';
import { FiltersService } from './filters.service';

export interface FilterPayload {
  startDate?: string;
  endDate?: string;
  taxAccount?: string;
  vehicles?: Vehicle[];
  jurisdictions?: string[];
  userReview?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FilterQuestionsService {
  constructor(private filtersService: FiltersService) {}

  createStartDateQuestion(
    options: DatePickerControlOptions
  ): DatePickerQuestion {
    const defaultQuestionOptions: DatePickerControlOptions = {
      key: 'startDate',
      label: 'Start Date',
      value: this.filtersService.getDefaultStartDate(),
      required: true,
      dateAfterOther: 'endDate',
      dateRangeDate: 'endDate',
      dateRangeLimit: 365,
      validators: [min(LocalDate.now().minusYears(9)), max(LocalDate.now())]
    };

    return new DatePickerQuestion({
      ...defaultQuestionOptions,
      ...{
        ...options,
        value: options.value || defaultQuestionOptions.value
      }
    });
  }

  createEndDateQuestion(options: DatePickerControlOptions): DatePickerQuestion {
    const defaultQuestionOptions: DatePickerControlOptions = {
      key: 'endDate',
      label: 'End Date',
      required: true,
      dateAfterOther: 'startDate',
      dateRangeDate: 'startDate',
      validators: [max(LocalDate.now())]
    };

    return new DatePickerQuestion({
      ...defaultQuestionOptions,
      ...{
        ...options,
        value: options.value || defaultQuestionOptions.value
      }
    });
  }

  createTaxAccountQuestion(
    taxAccounts: TaxAccount[],
    options: DropdownControlOptions
  ): DropdownQuestion {
    const defaultQuestionOptions: DropdownControlOptions = {
      key: 'taxAccount',
      label: 'Tax Account',
      readonly: false,
      columnClass: 'is-full',
      options: [
        { value: 'Any', key: '' },
        {
          value: 'Unassigned',
          key: 'unassigned'
        }
      ].concat(
        taxAccounts.map(taxAccount => ({
          key: taxAccount.id.value,
          value: taxAccount.companyName
        }))
      )
    };
    return new DropdownQuestion({
      ...defaultQuestionOptions,
      ...options
    });
  }

  createVehicleQuestion(
    vehicles: Vehicle[],
    options: any
  ): MultiselectQuestion<Vehicle> {
    const defaultQuestionOptions: MultiselectControlOptions<Vehicle> = {
      key: 'vehicles',
      label: 'Vehicles',
      readonly: false,
      order: 1,
      columnClass: 'is-full',
      required: true,
      options: vehicles,
      selectAllLabel: 'All Vehicles',
      noResultsLabel: 'No vehicles found.',
      keyMapper: vehicle => vehicle.id,
      labelMapper: vehicle => vehicle.customId,
      valueMapper: totalItems => `${totalItems} vehicles selected`
    };
    return new MultiselectQuestion<Vehicle>({
      ...defaultQuestionOptions,
      ...options
    });
  }

  createJurisdictionQuestion(options: any): MultiselectQuestion<string> {
    const defaultQuestionOptions: MultiselectControlOptions<string> = {
      key: 'jurisdictions',
      label: 'Jurisdiction',
      readonly: false,
      order: 2,
      columnClass: 'is-full',
      required: true,
      options: Object.keys(Jurisdictions),
      selectAllLabel: 'All Jurisdictions',
      noResultsLabel: 'No jurisdictions found.',
      keyMapper: jurisdiction => jurisdiction,
      labelMapper: jurisdiction => Jurisdictions[jurisdiction],
      valueMapper: totalItems => `${totalItems} jurisdictions selected`
    };
    return new MultiselectQuestion<Jurisdictions>({
      ...defaultQuestionOptions,
      ...options
    });
  }

  createUserReviewRequiredQuestion(options: CheckboxQuestionOptions): CheckboxQuestion {
    const defaultQuestionOptions: CheckboxQuestionOptions = {
      key: 'userReview',
      label: 'User Review Required',
      value: options.value,
      columnClass: 'is-full',
      order: 3,
      isValid: true
    };
    return new CheckboxQuestion({
      ...defaultQuestionOptions,
      ...options
    });
  }
}
